import React from 'react';
import { type Location } from 'react-router-dom';
import { NotificationBell } from '@/components/shared/NotificationBell';
import TimerWidget from '@/components/TimerWidget';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { SrIcon } from '@/components/ui/SrIcon';
import logoMark from '@/assets/logos/solvrev-logo-mark.png';
import AppMobileNav from './AppMobileNav';
import type { NavItem } from './nav-items';
import type { SidebarShortcut } from '@/hooks/use-sidebar-shortcuts';
import type { useAuth } from '@/contexts/AuthContext';
import type { useImpersonation } from '@/contexts/ImpersonationContext';

type AuthValue = ReturnType<typeof useAuth>;
type ImpersonationValue = ReturnType<typeof useImpersonation>;

interface AppHeaderProps {
  // Mobile drawer
  mobileDrawerOpen: boolean;
  setMobileDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  // Mobile nav
  filteredNav: NavItem[];
  location: Location;
  innstillingerOpen: boolean;
  setInnstillingerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleNavClick: () => void;
  shortcuts: SidebarShortcut[];
  isShortcutActive: (shortcutPath: string) => boolean;
  // Feedback dialog
  setFeedbackOpen: React.Dispatch<React.SetStateAction<boolean>>;
  // Auth / impersonation (read in AppLayout, passed down to preserve hook calls)
  user: AuthValue['user'];
  logout: AuthValue['logout'];
  isSuperadmin: AuthValue['isSuperadmin'];
  activeUser: ImpersonationValue['activeUser'];
  isImpersonating: ImpersonationValue['isImpersonating'];
  setImpersonatedUserId: ImpersonationValue['setImpersonatedUserId'];
  allUsers: ImpersonationValue['allUsers'];
  initials: string;
}

export default function AppHeader({
  mobileDrawerOpen,
  setMobileDrawerOpen,
  filteredNav,
  location,
  innstillingerOpen,
  setInnstillingerOpen,
  handleNavClick,
  shortcuts,
  isShortcutActive,
  setFeedbackOpen,
  user,
  logout,
  isSuperadmin,
  activeUser,
  isImpersonating,
  setImpersonatedUserId,
  allUsers,
  initials,
}: AppHeaderProps) {
  return (
    <header className="h-[58px] bg-card border-b-[1.5px] border-[var(--sr-revemoerk)] flex items-center justify-between px-4 md:px-6 shrink-0">
      {/* Mobile: brand + hamburger */}
      <div className="flex items-center gap-3 md:hidden">
        <img src={logoMark} alt="Sølvrev" className="h-7 w-auto" />
        <Sheet open={mobileDrawerOpen} onOpenChange={setMobileDrawerOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="Åpne meny">
              <SrIcon name="menu" size={20} />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 bg-sidebar text-sidebar-foreground">
            <AppMobileNav
              filteredNav={filteredNav}
              location={location}
              innstillingerOpen={innstillingerOpen}
              setInnstillingerOpen={setInnstillingerOpen}
              handleNavClick={handleNavClick}
              shortcuts={shortcuts}
              isShortcutActive={isShortcutActive}
            />
          </SheetContent>
        </Sheet>
      </div>

      {/* TimerWidget centered on md+ */}
      <div className="hidden md:flex flex-1 items-center justify-center">
        <TimerWidget />
      </div>

      {/* Right cluster */}
      <div className="flex items-center gap-2 md:gap-3">
        {isImpersonating && (
          <div className="hidden sm:inline-flex items-center gap-2 rounded-full bg-[#FEF3C7] text-[#92400E] text-[11.5px] font-medium px-[10px] py-1">
            <SrIcon name="eye" size={14} />
            <span>Viser som: {activeUser?.full_name}</span>
            <button
              onClick={() => setImpersonatedUserId(null)}
              className="ml-0.5 hover:text-amber-900"
              aria-label="Avslutt impersonasjon"
            >
              <SrIcon name="x-circle" size={14} />
            </button>
          </div>
        )}

        {isSuperadmin && (
          <Select
            value={activeUser?.id ?? ''}
            onValueChange={(val) => {
              const selfId = allUsers.find((u) => u.email === user?.email)?.id;
              setImpersonatedUserId(val === selfId ? null : val);
            }}
          >
            <SelectTrigger className="h-8 w-[180px] text-xs hidden sm:flex border-[1.5px] border-[var(--sr-revemoerk)] rounded-md">
              <SrIcon name="eye" size={14} className="mr-1.5" />
              <SelectValue placeholder="Vis som..." />
            </SelectTrigger>
            <SelectContent>
              {allUsers.map((u) => (
                <SelectItem key={u.id} value={u.id}>
                  <div className="flex items-center gap-2">
                    {u.avatar_url ? (
                      <img src={u.avatar_url} alt="" className="w-4 h-4 rounded-full" />
                    ) : (
                      <span className="w-4 h-4 rounded-full bg-primary/10 text-primary text-[8px] font-medium flex items-center justify-center">
                        {u.full_name[0]}
                      </span>
                    )}
                    {u.full_name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setFeedbackOpen(true)}
              aria-label="Send tilbakemelding"
            >
              <SrIcon name="megaphone" size={16} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Send tilbakemelding</TooltipContent>
        </Tooltip>
        <NotificationBell />

        <div className="hidden sm:flex items-center gap-3">
          {/* VIB-998-followup: header-avataren viste alltid initialer fordi
              AuthUser ikke har avatar_url. Bruk activeUser (UserLookup) som
              har bildet; fall tilbake til initialer. */}
          {activeUser?.avatar_url ? (
            <img
              src={activeUser.avatar_url}
              alt={activeUser.full_name}
              title={activeUser.full_name}
              className="w-8 h-8 rounded-full object-cover border-[1.5px] border-[var(--sr-revemoerk)]"
            />
          ) : (
            <span
              className="w-8 h-8 rounded-full bg-[var(--sr-revegraa)] border-[1.5px] border-[var(--sr-revemoerk)] flex items-center justify-center text-[13px] font-semibold"
              title={activeUser?.full_name ?? user?.name}
            >
              {initials}
            </span>
          )}
          <Button variant="ghost" size="icon" onClick={logout} title="Logg ut">
            <SrIcon name="logout" size={16} />
          </Button>
        </div>
        <Button variant="ghost" size="icon" onClick={logout} className="sm:hidden" title="Logg ut">
          <SrIcon name="logout" size={16} />
        </Button>
      </div>
    </header>
  );
}
